'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';

export default function UnifiedLoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const { user } = await signIn(email, password);
      
      if (!user) {
        throw new Error('Erro ao efetuar login. Verifique suas credenciais.');
      }

      // Get user profile to determine role
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      // Redirect based on role
      if (profile?.role === 'admin') {
        router.push('/admin/dashboard');
      } else {
        router.push('/client/dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setErrorMessage(
        error.message || 'Erro ao efetuar login. Verifique suas credenciais e tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-black tracking-tighter uppercase text-foreground mb-4">
            Login
          </h1>
          <p className="text-muted-foreground">
            Digite seu email e senha para acessar o sistema.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card p-8 space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wider mb-3 text-foreground">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-6 py-4 bg-background border-2 border-border rounded-none text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
              placeholder="seu@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-bold uppercase tracking-wider mb-3 text-foreground">
              Senha
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-6 py-4 bg-background border-2 border-border rounded-none text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="flex items-center gap-3 p-4 bg-error/10 border-2 border-error text-error">
              <Icon name="ExclamationTriangleIcon" size={24} />
              <p className="text-sm font-bold">{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary text-primary-foreground py-5 text-[11px] font-black uppercase tracking-[0.3em] hover:bg-accent transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Entrando...' : 'Entrar'}
          </button>

          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Não tem conta?{' '}
              <button
                type="button"
                onClick={() => router.push('/client/register')}
                className="text-primary font-bold hover:underline"
              >
                Registrar-se
              </button>
            </p>
            
           
          </div>
        </form>
      </div>
    </div>
  );
}